import { LOCAL_MODE } from '../../../Config';

import {
  Object3D,
  Vector2,
  Vector3,
  TextureLoader,
  SpriteMaterial,
  Sprite,
  MeshBasicMaterial,
  Mesh,
  LinearFilter,
  PlaneGeometry,
  Raycaster,
} from 'three';

import { gsap, Power1 } from 'gsap';
import {
  getArea,
  reduceEqually,
  toScreenPosition,
  vecToScreenPosition,
  isLeft,
  distanceFromSegment,
  getPointInBetween,
} from '../utils/GeomUtils';

// import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry';
// import { Line2 } from 'three/examples/jsm/lines/Line2';
// import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
const EventEmitter = require('events');

class ImageBendManager extends EventEmitter {
  constructor(raycaster, camera) {
    super();

    this.raycaster = raycaster;
    this.camera = camera;
    this.sphere = null;

    this.scale = 20.0;
    this.color = '#FFFFFF';

    this.showAnchorsOnSelected = true;
    this.focusAnimating = false;
    this.loading = false;

    this.opacityTweens = [];

    this.currentIdList = [];

    this.loadSprites();
    this.init();
  }

  init() {
    this.containers = [];
    this.planes = [];
    // --- containers
    this.currentContainer = null;

    this.selectedAnchor = null;
    this.selectedImage = null;
    this.clickedPosition = null;
    this.clickedPositionToImage = null;
    this.clickedAnchorsPositions = [];

    this.backgroundTexture = null;
    this.lastDragPos = new Vector2();
  }

  setBackgroundTexture(texture) {
    this.backgroundTexture = texture;
  }

  setSphere(sphere) {
    this.sphere = sphere;
  }

  loadSprites() {
    const tex = new TextureLoader().load(
      'street-gallery-bts/assets/sprites/circle.png',
      () => {
        this.circleMaterial = new SpriteMaterial({
          map: tex,
          depthTest: false,
          color: 0xff0000,
          sizeAttenuation: false,
        });
      },
    );

    const consTex = new TextureLoader().load(
      'street-gallery-bts/assets/sprites/constraint.png',
      () => {
        this.circleConstraintMaterial = new SpriteMaterial({
          map: consTex,
          depthTest: false,
          color: 0xffffff,
          sizeAttenuation: false,
        });
      },
    );

    const texRed = new TextureLoader().load(
      'street-gallery-bts/assets/sprites/circle.png',
      () => {
        this.circleConstraintMaterialRed = new SpriteMaterial({
          map: texRed,
          depthTest: false,
          color: 0xff0000,
          sizeAttenuation: false,
        });
      },
    );
  }

  getPlaneSize(texture) {
    const maxSize = 350;
    const max = Math.max(texture.image.width, texture.image.height);

    let planeWidth = texture.image.width;
    let planeHeight = texture.image.height;

    if (max > maxSize) {
      const ratio = Math.min(
        maxSize / texture.image.width,
        maxSize / texture.image.height,
      );
      planeWidth = texture.image.width * ratio;
      planeHeight = texture.image.height * ratio;
    }

    return { width: planeWidth, height: planeHeight };
  }

  addImage(item, data, fromGallery, onComplete) {
    if (!LOCAL_MODE && item.image_url && !item.image_url.includes('http')) {
      item.image_url = window.CONFIG.backendURL + item.image_url;
    } else {
      item.image_url = item.image_url.replace(/^http:\/\//i, 'https://');
    }
    const imagePath = item.src || item.image_url;
    const loader = new TextureLoader();

    const nSegments = 3;
    var texture = loader.load(
      imagePath,
      () => {
        if(this.currentIdList.indexOf(item.asset_id) === -1 && !fromGallery) {
          return;
        }
        const size = this.getPlaneSize(texture);

        let geometry = new PlaneGeometry(
          size.width,
          size.height,
          nSegments,
          nSegments,
        );

        if (data.vertices) {
          // assert if not same length
          if (geometry.vertices.length !== data.vertices.length) {
            console.error(
              'Vertices count is not the same. Prepare for the unexpected',
            );
            return;
          }
        }

        const material = new MeshBasicMaterial({
          color: 0xffffff,
          map: texture,
          wireframe: false,
          transparent: true,
          depthTest: false,
          opacity: 0,

          // blending: AdditiveBlending,
        });
        material.map.minFilter = LinearFilter;

        const plane = new Mesh(geometry, material);
        plane.userData.serialize = true;
        plane.userData.texture = `/street-gallery-bts/assets/artworks/${item.asset_id}`;
        //plane.userData.texture = item.image_url || item.src;
        plane.userData.itemId = item.asset_id;

        // this is used only when using undo
        if (item.uuid) {
          plane.uuid = item.uuid;
        }

        this.containers.push(plane);
        this.currentContainer.add(plane);

        // if it is a saved one
        if (data.vertices) {
          // assert if not same length
          if (geometry.vertices.length !== data.vertices.length) {
            console.error(
              'Vertices count is not the same. Prepare for the unexpected',
            );
          }

          data.vertices.forEach((vertex, index) => {
            geometry.vertices[index].set(vertex.x, vertex.y, vertex.z);
          });

          geometry.verticesNeedUpdate = true;
          geometry.boundingSphere = null;
          geometry.boundingBox = null;
          plane.updateMatrix();
          plane.updateMatrixWorld();
        }

        if (data.container) {
          plane.position.x = data.container.position.x;
          plane.position.y = data.container.position.y;
          plane.position.z = data.container.position.z;

          plane.rotation.x = data.container.rotation._x;
          plane.rotation.y = data.container.rotation._y;
          plane.rotation.z = data.container.rotation._z;

          if (data.container.data.opacity) {
            plane.userData.opacity = data.container.data.opacity;
          }
        } else {
          // we fakely place the plane
          const sphereRadius = this.sphere.geometry.boundingSphere.radius;
          const pLocal = new Vector3(0, 0, -sphereRadius);
          const target = pLocal.applyMatrix4(this.camera.matrixWorld);
          plane.position.copy(target);
          plane.quaternion.copy(this.camera.quaternion);

          for (let i = 0; i < geometry.vertices.length; i++) {
            const localVertex = geometry.vertices[i].clone();
            localVertex.z = -this.sphereRadius;
            const directionVector = this.camera.getWorldDirection(
              new Vector3(),
            );
            directionVector.subVectors(this.sphere.position, localVertex);
            directionVector.normalize();

            const ray = new Raycaster(localVertex, directionVector);
            const collisionResults = ray.intersectObject(this.sphere);
            if (collisionResults.length > 0) {
              geometry.vertices[i].z =
                collisionResults[0].point.z + sphereRadius;
            }
          }

          // we take the vertices positions in world
          // plane.geometry.vertices
          plane.updateMatrixWorld();

          plane.geometry.vertices.forEach((vertex, index) => {
            const v = new Vector3().copy(vertex);
            plane.localToWorld(v);
            plane.geometry.vertices[index].set(v.x, v.y, v.z);
          });

          plane.geometry.verticesNeedUpdate = true;
          plane.position.set(0, 0, 0);
          plane.rotation.set(0, 0, 0);
          plane.updateMatrix();

          // opacity up
          plane.userData.opacity = 0.95;
          gsap.to(plane.material, {
            opacity: 0.95,
            duration: 0.5,
            ease: Power1.easeInOut,
            onComplete: () => {
              this.loading = false;
            },
          });
        }

        geometry.verticesNeedUpdate = true;
        this.planes.push(plane);
        this.addVerticesAnchors(plane);

        if (fromGallery) {
          this.selectedImage = plane;
          this.show();
        }

        if (onComplete) onComplete(plane);
      },
      null,
      (err) => {
        console.log(err);
        if (onComplete) onComplete(null);
      },
    );
  }

  addVerticesAnchors(plane) {
    plane.userData.anchors = [];

    plane.geometry.vertices.forEach((item, index) => {
      // var width = this.circleMaterial.map.image.width;
      // var height = this.circleMaterial.map.image.height;

      const sprite = new Sprite(this.circleConstraintMaterial);
      sprite.type = 'Anchor';
      let px = 48;
      let sc = (0.1375 * px) / 64.0; // magic number ?
      sprite.scale.set(sc, sc, 1);

      sprite.position.x = item.x;
      sprite.position.y = item.y;
      sprite.position.z = item.z - 1;

      sprite.userData.serialize = false;
      sprite.userData.vertexId = index;
      sprite.userData.planeId = this.planes.length - 1;
      sprite.userData.planeRef = plane;
      sprite.userData.id = index;

      sprite.visible = false;

      plane.add(sprite);

      const constraint = new Sprite(this.circleConstraintMaterial);
      px = 120;
      sc = (0.1375 * px) / 64.0; // magic number ?
      constraint.scale.set(sc, sc, 1);

      constraint.position.x = item.x;
      constraint.position.y = item.y;
      constraint.position.z = item.z - 1.1;

      constraint.userData.serialize = false;
      constraint.userData.vertexId = index;
      constraint.userData.planeId = this.planes.length - 1;
      constraint.userData.anchorRef = sprite;

      constraint.userData.id = index;

      constraint.visible = false;

      plane.add(constraint);

      sprite.userData.constraintRef = constraint;

      plane.userData.anchors.push(sprite);
    });
  }

  showPlanes(delay = 0, delayInBtw = 0.05, duration = 0.8) {
    if (!this.currentContainer || this.currentContainer.userData.mode !== 1)
      return;

    this.opacityTweens.forEach((tw) => {
      tw.kill();
    });

    this.opacityTweens = [];
    this.planes.forEach((plane, index) => {
      const opacity = plane.userData.opacity ? plane.userData.opacity : 0.95;

      const tween = gsap.to(plane.material, {
        opacity,
        duration: duration,
        delay: delay + index * delayInBtw,
        ease: Power1.out,

        onComplete: () => {},
      });
      this.opacityTweens.push(tween);
    });
  }

  show() {
    if (!this.currentContainer || this.currentContainer.userData.mode !== 1)
      return;

    const corners = [0, 3, 12, 15];
    this.currentContainer.children.forEach((item, index) => {
      item.children.forEach((child) => {
        if (this.showAnchorsOnSelected) {
          if (child.type === 'Anchor' && corners.includes(child.userData.id)) {
            child.visible =
              child.userData.planeRef.id === this.selectedImage.id;
          }
        } else if (
          child.type === 'Anchor' &&
          corners.includes(child.userData.id)
        ) {
          child.visible = true;
        }
      });
    });
  }

  hide() {
    if (!this.currentContainer || this.currentContainer.userData.mode !== 1)
      return;

    this.currentContainer.children.forEach((item, index) => {
      item.children.forEach((child) => {
        if (this.showAnchorsOnSelected) {
          if (child && child.type === 'Anchor') {
            child.visible = false;
          }
        }
      });
    });
  }

  checkFocusState() {
    // if animating do nothing
    if (!this.selectedImage || this.focusAnimating || this.loading)
      return false;

    if (!this.savedState && this.selectedImage) {
      this.focusImage();
      return true;
    }

    if (this.savedState) {
      this.unfocusImage();
      return false;
    }
  }

  focusImage() {
    this.focusAnimating = true;
    // save everything
    this.savedState = {
      plane: this.selectedImage,
      vertices: JSON.parse(
        JSON.stringify(this.selectedImage.geometry.vertices, ['x', 'y', 'z']),
      ),
      position: new Vector3().copy(this.selectedImage.position),
      quaternion: this.selectedImage.quaternion.clone(),
    };

    const size = this.getPlaneSize(this.selectedImage.material.map);
    const nSegments = 3;

    const geometry = new PlaneGeometry(
      size.width,
      size.height,
      nSegments,
      nSegments,
    );

    const { material } = this.selectedImage;

    // we'll create a plane on front – get vertices pos and then remove it
    const container = new Object3D();
    this.currentContainer.add(container);

    let plane = new Mesh(geometry, material);
    container.add(plane);

    const sphereRadius = this.sphere.geometry.boundingSphere.radius;
    const pLocal = new Vector3(0, 0, -sphereRadius);
    const target = pLocal.applyMatrix4(this.camera.matrixWorld);

    plane.position.copy(target);
    plane.quaternion.copy(this.camera.quaternion);
    plane.visible = false;
    plane.updateMatrixWorld();
    const vertices = [];

    for (let i = 0; i < geometry.vertices.length; i++) {
      const localVertex = geometry.vertices[i].clone();
      localVertex.z = -this.sphereRadius;
      const directionVector = this.camera.getWorldDirection(new Vector3());
      directionVector.subVectors(this.sphere.position, localVertex);
      directionVector.normalize();

      const ray = new Raycaster(localVertex, directionVector);
      const collisionResults = ray.intersectObject(this.sphere);

      const copy = new Vector3().copy(geometry.vertices[i]);
      plane.localToWorld(copy);

      if (collisionResults.length > 0) {
        copy.z = collisionResults[0].point.z + sphereRadius;
      }
      vertices.push(copy);
    }
    container.remove(plane);
    this.currentContainer.remove(container);
    plane = null;

    const duration = 0.35;
    vertices.forEach((vertex, index) => {
      gsap.to(this.selectedImage.geometry.vertices[index], {
        x: vertex.x,
        y: vertex.y,
        z: vertex.z,
        duration,
        overwrite: 1,
        delay: 0.1 + index * 0.00005,
        ease: Power1.easeInOut,
        onComplete: () => {
          if (index === vertices.length - 1) this.focusAnimating = false;
        },
        onUpdate: () => {
          this.selectedImage.geometry.verticesNeedUpdate = true;
        },
      });
    });

    this.opacityTweens.forEach((tw) => {
      if (tw) tw.kill();
    });
    this.opacityTweens = [];
    this.planes.forEach((p) => {
      let tween;
      if (p.uuid !== this.selectedImage.uuid) {
        tween = gsap.to(p.material, {
          opacity: 0,
          overwrite: 1,
          // clearProps: 'all',
          duration: duration * 0.5,
          ease: Power1.easeInOut,
        });
      } else {
        tween = gsap.to(p.material, {
          opacity: 1,
          overwrite: 1,
          // clearProps: 'all',
          duration: duration * 0.5,
          ease: Power1.easeInOut,
        });
      }

      this.opacityTweens.push(tween);
    });
    this.hide();
    this.emit('focus');
  }

  unfocusImage() {
    if (!this.savedState) return;

    this.focusAnimating = true;
    const { plane } = this.savedState;
    const len = this.savedState.vertices.length;

    this.savedState.vertices.forEach((vertex, index) => {
      gsap.to(plane.geometry.vertices[index], {
        x: vertex.x,
        y: vertex.y,
        z: vertex.z,
        duration: 0.5,
        delay: index * 0.0005,
        ease: Power1.easeOut,
        onComplete: () => {
          if (index === len - 1) this.focusAnimating = false;
        },
        onUpdate: () => {
          plane.geometry.verticesNeedUpdate = true;
        },
      });
    });

    this.opacityTweens.forEach((tw) => {
      if (tw) tw.kill();
    });
    this.opacityTweens = [];
    this.planes.forEach((p) => {
      const opacity = p.userData.opacity ? p.userData.opacity : 0.95;
      let tween = gsap.to(p.material, {
        opacity,
        duration: 1,
        ease: Power1.easeInOut,
      });
      this.opacityTweens.push(tween);
    });
    this.savedState = null;
    this.hide();
    this.emit('unfocus');
  }

  getAnchorSprite() {
    const intersects = this.raycaster.intersectObjects(
      this.currentContainer.children,
      true,
    );

    const sortByRenderOrder = (a, b) => {
      return b.object.renderOrder - a.object.renderOrder;
    };
    intersects.sort(sortByRenderOrder);

    let i;
    for (i = 0; i < intersects.length; i++) {
      if (intersects[i].object.type === 'Anchor') {
        return intersects[i];
      }
    }
    // else we have a mesh
    for (i = 0; i < intersects.length; i++) {
      if (intersects[i].object.type === 'Mesh') {
        return intersects[i];
      }
    }

    return null;
  }

  placeImageOnFront() {
    const plane = this.selectedImage || this.selectedAnchor.parent;

    // get position in index
    let indexInChildren = -1;
    this.currentContainer.children.forEach((item, index) => {
      if (item.children.length && plane.uuid === item.uuid)
        indexInChildren = index;
    });

    if (indexInChildren >= 0) {
      this.currentContainer.children.push(
        this.currentContainer.children.splice(indexInChildren, 1)[0],
      );
    }
  }

  onMousePressed(position, raycaster) {
    if (!position || this.loading) return;
    if (this.savedState) return;

    this.selectedAnchor = null;
    this.selectedImage = null;

    this.clickedPosition = position;

    this.lastDragPos = new Vector2(position.xCentered, position.yCentered);

    // create an object
    const result = this.getAnchorSprite(raycaster);

    if (!result) {
      /*
      gsap.to(this.camera.position, {
        z: -1500,
        duration: 1,
        delay: 0,
        ease: Power1.easeInOut,
        onComplete: () => {
          gsap.to(this.camera.position, {
            z: 0,
            duration: 1,
            delay: 5,
            ease: Power1.easeInOut,
            onComplete: () => {},
          });
        },
      });
      */
    }

    if (result && result.object.type === 'Anchor') {
      this.selectedAnchor = result.object;
      this.placeImageOnFront(this.selectedAnchor.parent);
      return this.selectedAnchor;
    }
    if (result && result.object.type === 'Mesh') {
      this.selectedImage = result.object;
      this.selectedImage.geometry.computeBoundingBox();

      this.vertexDiffPos = [];
      this.selectedImage.userData.anchors.forEach((anchor) => {
        const onScreenPos = toScreenPosition(anchor, this.camera);
        const onScreenMousPos = new Vector2(
          position.screenMouseX,
          position.screenMouseY,
        );
        this.vertexDiffPos.push(
          new Vector2(
            onScreenPos.x - onScreenMousPos.x,
            onScreenPos.y - onScreenMousPos.y,
          ),
        );
        if (this.showAnchorsOnSelected) this.show();
      });

      this.placeImageOnFront(this.selectedImage);

      return this.selectedImage;
    }
    this.selectedAnchor = null;
    this.selectedImage = null;
    if (this.showAnchorsOnSelected) this.hide();
    return null;
  }

  onMouseMove(position, raycaster) {
    return this.getAnchorSprite(raycaster);
  }

  onMouseDrag(position) {
    if (this.selectedAnchor) {
      const intersects = this.raycaster.intersectObject(this.sphere);

      if (intersects.length) {
        const pnt = intersects[0].point;
        const vertexIndex = this.selectedAnchor.userData.vertexId;
        const plane = this.selectedAnchor.parent;

        if (intersects.length) {
          const v = new Vector3().copy(pnt);
          const prevPos = new Vector3().copy(this.selectedAnchor.position);

          this.selectedAnchor.position.set(v.x, v.y, v.z);
          this.alignAnchors2(plane);

          if (!this.checkArea(vertexIndex, new Vector3().copy(v))) {
            //console.log('area too big');
            this.selectedAnchor.position.set(prevPos.x, prevPos.y, prevPos.z);
            return;
          }

          if (!this.checkDistances(vertexIndex, new Vector3().copy(v))) {
            //console.log('distance too big');
            this.selectedAnchor.position.set(prevPos.x, prevPos.y, prevPos.z);
            this.selectedAnchor.material = this.circleConstraintMaterialRed;
            return;
          }

          this.selectedAnchor.userData.constraintRef.visible = false;
          this.selectedAnchor.material = this.circleConstraintMaterial;

          plane.userData.anchors.forEach((item, index) => {
            const pntOnScreen = toScreenPosition(item, this.camera);
            const vertexPos = new Vector2();
            vertexPos.x = (pntOnScreen.x / window.innerWidth) * 2 - 1;
            vertexPos.y = -(pntOnScreen.y / window.innerHeight) * 2 + 1;
            this.raycaster.setFromCamera(vertexPos, this.camera);
            const intersects = this.raycaster.intersectObject(
              this.sphere,
              false,
            );
            if (intersects.length) {
              const v = intersects[0].point;
              item.position.set(v.x, v.y, v.z);
              plane.geometry.vertices[index].set(v.x, v.y, v.z);
            }
          });

          plane.geometry.verticesNeedUpdate = true;
          plane.geometry.boundingSphere = null;
          plane.geometry.boundingBox = null;
        }
      }
    }

    if (this.selectedImage) {
      const plane = this.selectedImage;
      this.placeVerticesOnSphere(plane, position);
    }
  }

  placeVerticesOnSphere(plane, position) {
    plane.userData.anchors.forEach((anchor, index) => {
      // get screen pos
      const onScreenMousPos = new Vector2(
        position.screenMouseX,
        position.screenMouseY,
      );

      // foreach vertex get world position relative to mouse
      const vertexRelativePos = new Vector2(
        onScreenMousPos.x + this.vertexDiffPos[index].x,
        onScreenMousPos.y + this.vertexDiffPos[index].y,
      );

      // convert for raycast
      const vertexPos = new Vector2();
      vertexPos.x = (vertexRelativePos.x / window.innerWidth) * 2 - 1;
      vertexPos.y = -(vertexRelativePos.y / window.innerHeight) * 2 + 1;

      // let ray = new Raycaster();
      this.raycaster.setFromCamera(vertexPos, this.camera);

      // get intersection for each point
      const intersects = this.raycaster.intersectObject(this.sphere);
      if (intersects.length) {
        const pnt = intersects[0].point;

        plane.geometry.vertices[index].set(pnt.x, pnt.y, pnt.z);
        anchor.position.set(pnt.x, pnt.y, pnt.z);

        plane.geometry.verticesNeedUpdate = true;
        plane.geometry.boundingSphere = null;
        plane.geometry.boundingBox = null;
        plane.updateMatrix();
        plane.updateMatrixWorld();
      }
    });
  }

  onCameraDrag() {
    if (
      this.camera &&
      this.currentContainer.children &&
      this.raycaster &&
      !this.selectedAnchor &&
      !this.selectedImage
    ) {
      const ray = new Raycaster();
      ray.set(
        this.camera.getWorldPosition(new Vector3()),
        this.camera.getWorldDirection(new Vector3()),
      );
      const intersects = ray.intersectObjects(
        this.currentContainer.children,
        true,
      );
      for (let i = 0; i < intersects.length; i++) {
        if (intersects[i].object.type === 'Mesh') {
          return intersects[i].object;
        }
      }
    }
    return null;
  }

  alignAnchors2(plane) {
    const anchors = plane.userData.anchors;

    this.alignAnchorLine(anchors, [0, 1, 2, 3]);
    this.alignAnchorLine(anchors, [3, 7, 11, 15]);
    this.alignAnchorLine(anchors, [0, 4, 8, 12]);
    this.alignAnchorLine(anchors, [12, 13, 14, 15]);
    this.alignAnchorLine(anchors, [4, 5, 6, 7]);
    this.alignAnchorLine(anchors, [8, 9, 10, 11]);

    return;
  }

  alignAnchorLine(anchors, vertices) {
    let startPos = anchors[vertices[0]].position;
    let endPos = anchors[vertices[vertices.length - 1]].position;

    for (let i = 1; i < vertices.length - 1; i++) {
      const vIndex = vertices[i];
      let current = anchors[vIndex];
      let pos = getPointInBetween(startPos, endPos, i / (vertices.length - 1));
      current.position.set(pos.x, pos.y, pos.z);
    }
  }

  checkDistances(vertexIndex, pos) {
    const plane = this.selectedAnchor.parent;

    const corners = [0, 3, 12, 15];
    // get screen positions
    const screenPositions = [];
    plane.userData.anchors.forEach((anchor, index) => {
      if (corners.includes(index)) {
        const onScreenPos = toScreenPosition(anchor, this.camera);
        screenPositions.push(onScreenPos);
      }
    });

    // replace moving pos
    const screenPositionIndex = corners.indexOf(vertexIndex);
    screenPositions[screenPositionIndex] = vecToScreenPosition(
      pos,
      this.camera,
    );

    // ensure that distance is not more than 3x the original
    for (let i = 0; i < screenPositions.length; i++) {
      if (i > 0) {
        const a = screenPositions[i - 1];
        const b = screenPositions[i];
        const dist = a.distanceTo(b);
        if (
          dist > 900 &&
          (i === screenPositionIndex || i - 1 === screenPositionIndex)
        ) {
          //console.log(`distance ${dist}`);
          // return false;
        }
      }
    }

    const segments = [];
    screenPositions.forEach((anchor, index) => {
      if (index !== screenPositionIndex) {
        segments.push(screenPositions[index]);
      }
    });

    // is originally left or right ?
    const originalAnchor = toScreenPosition(
      this.selectedAnchor.userData.constraintRef,
      this.camera,
    );

    let isInbounds = true;
    segments.forEach((p, index) => {
      const a = index > 0 ? index - 1 : segments.length - 1;
      const b = index;
      const scaledSegment = reduceEqually(segments[a], segments[b], 9999);

      const dist = distanceFromSegment(
        screenPositions[screenPositionIndex].x,
        screenPositions[screenPositionIndex].y,
        scaledSegment[0].x,
        scaledSegment[0].y,
        scaledSegment[1].x,
        scaledSegment[1].y,
      );

      if (dist < 35) {
        //console.log('distance from line' + dist + ' at segment ' + index);
        isInbounds = false;
        return;
      }

      const left = isLeft(segments[a], segments[b], originalAnchor);
      const isOtherSide = isLeft(
        segments[a],
        segments[b],
        screenPositions[screenPositionIndex],
      );

      if (left !== isOtherSide) {
        // console.log(`wrong side from line ${index}`);
        isInbounds = false;
      }
    });

    return isInbounds;
  }

  checkArea(vertexIndex, pos) {
    const plane = this.selectedAnchor.parent;

    // get screen positions

    const corners = [0, 3, 12, 15];

    const screenPositions = [];
    plane.userData.anchors.forEach((anchor, index) => {
      if (corners.includes(index)) {
        const onScreenPos = toScreenPosition(anchor, this.camera);
        screenPositions.push(onScreenPos);
      }
    });

    // replace moving pos
    screenPositions[corners.indexOf(vertexIndex)] = vecToScreenPosition(
      pos,
      this.camera,
    );

    // we need to set clockwise
    const pnt2 = screenPositions[2];
    screenPositions[2] = screenPositions[3];
    screenPositions[3] = pnt2;

    const area = getArea(screenPositions);
    return area < 500000;
  }

  onMouseUp() {
    if (this.selectedAnchor) {
      const plane = this.selectedAnchor.parent;
      this.alignAnchors2(plane);

      plane.userData.anchors.forEach((anchor, index) => {
        anchor.material = this.circleConstraintMaterial;
        if (index !== this.selectedAnchor.userData.id) {
          anchor.userData.constraintRef.position.x = anchor.position.x;
          anchor.userData.constraintRef.position.y = anchor.position.y;
          anchor.userData.constraintRef.position.z = anchor.position.z;
          anchor.userData.constraintRef.visible = false;
        }
      });
    }

    this.clickedPositionToImage = null;
  }

  clearSelected() {
    this.selectedImage = null;
    this.selectedAnchor = null;
    this.hide();
  }

  setOpacity(pct) {
    const plane = this.getSelected();
    if (plane) {
      const normalized = pct / 100;
      plane.userData.opacity = normalized;
      plane.material.opacity = normalized;
    }
  }

  getSelected() {
    return this.selectedImage || this.selectedAnchor.parent;
  }

  deleteSelected() {
    const plane = this.getSelected();
    if (plane) {
      //plane.visible = false;
      this.currentContainer.remove(plane);

      for (var i = 0; i < this.planes.length; i++) {
        if (this.planes[i].uuid === plane.uuid) {
          this.planes.splice(i, 1);
        }
      }

      return true;
    }
    return false;
  }

  getPlanesIdList() {
    let result = [];
    this.currentContainer.children.forEach((object, index) => {
      result.push(object.userData.itemId);
    });
    return result;
  }

  getPlaneForAssetId(assetId) {
    let result = null;
    this.currentContainer.children.forEach((object, index) => {
      if (object.userData.itemId === assetId) result = object;
    });
    return result;
  }

  getPlaneForUUID(uuid) {
    let result = null;
    this.currentContainer.children.forEach((object, index) => {
      if (object.uuid === uuid) result = object;
    });
    return result;
  }

  getNumPlanes() {
    return this.planes.length;
  }

  undo() {}
}

export default ImageBendManager;
